import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Bill = {
  id: number
  number: string
  vendor_reference: string
  partner: number
  partner_name?: string
  bill_date: string
  due_date: string | null
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID'
  currency_code: string
  total: string
  amount_due: string
  journal_entry: number | null
  journal_entry_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  number: string
  vendor_reference: string
  partner: number | 0
  bill_date: string
  due_date: string | null
  currency_code: string
}

const STATUS_COLOR: Record<Bill['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-blue-100 text-blue-800',
  PAID: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-700',
}

const STATUS_FILTERS: ('ALL' | Bill['status'])[] = ['ALL', 'DRAFT', 'OPEN', 'PAID', 'VOID']

export default function FinanceBills() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const billsQ = useQuery({
    queryKey: ['finance', 'bills'],
    queryFn: async () => (await api.get<Bill[]>('/finance/bills/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Bill['status']>('ALL')

  function openCreate() {
    setDraft({
      ...emptyDraft(),
      number: `BILL-${Date.now().toString().slice(-6)}`,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Bill>('/finance/bills/', {
      ...draft,
      partner: Number(draft.partner),
      status: 'DRAFT',
      notes: '',
      lines: [],
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
      toast.success('Bill created')
      setCreating(false)
      navigate(`/admin/finance/bills/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/bills/${id}/`),
    onSuccess: () => {
      toast.success('Bill deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const postMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/finance/bills/${id}/post_to_gl/`)).data,
    onSuccess: () => {
      toast.success('Posted to General Ledger')
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const bills = billsQ.data || []
  const vendors = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'VENDOR' || p.partner_type === 'BOTH')
  )
  const bankAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'ASSET' && a.code.startsWith('11')
  )
  const filtered = filterStatus === 'ALL'
    ? bills
    : bills.filter((b) => b.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Bills</h2>
          <p className="text-sm text-muted-foreground">
            {bills.length} total · {bills.filter((b) => b.status === 'OPEN').length} open
          </p>
        </div>
        <Button onClick={openCreate} disabled={vendors.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New bill
        </Button>
      </div>

      {vendors.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          Add at least one active <strong>vendor</strong> under Partners before creating bills.
        </div>
      )}

      {bankAccounts.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          No bank accounts configured (ASSET accounts with codes starting with 11). Quick pay won't be available until at least one exists.
        </div>
      )}

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {billsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No bills.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Vendor ref</Th>
                <Th>Date</Th>
                <Th>Vendor</Th>
                <Th>Status</Th>
                <Th className="text-right">Due</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((b) => (
                <tr
                  key={b.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/finance/bills/${b.id}`)}
                >
                  <Td className="font-mono text-xs">{b.number}</Td>
                  <Td className="font-mono text-xs">{b.vendor_reference || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{b.bill_date}</Td>
                  <Td>{b.partner_name}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[b.status]}`}>{b.status}</span>
                      {b.journal_entry_number && (
                        <span className="text-xs text-muted-foreground font-mono">{b.journal_entry_number}</span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right font-mono text-xs">
                    <span className={Number(b.amount_due) > 0 && b.status === 'OPEN' ? 'text-amber-700' : 'text-muted-foreground'}>
                      {Number(b.amount_due).toFixed(2)}
                    </span>
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/finance/bills/${b.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {b.status === 'DRAFT' && !b.journal_entry && (
                          <DropdownMenuItem
                            onClick={() => confirm(
                              `Post bill ${b.number} to General Ledger`,
                              () => postMut.mutate(b.id),
                            )}
                            disabled={postMut.isPending}
                            className="text-green-700"
                          >
                            Post to GL
                          </DropdownMenuItem>
                        )}
                        {b.status === 'OPEN' && bankAccounts.length > 0 && (
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/finance/bills/${b.id}`)}
                            className="text-purple-700"
                          >
                            Quick pay…
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!!b.journal_entry}
                          onClick={() => !b.journal_entry && confirm(
                            `bill ${b.number}`,
                            () => deleteMut.mutate(b.id),
                          )}
                          className={!b.journal_entry ? 'text-destructive' : ''}
                        >
                          {b.journal_entry ? 'Delete (posted)' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Lines + notes filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New bill"
        description="Create the bill header. Add line items, notes, and post to the General Ledger on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="bl-num">Number *</FieldLabel>
              <Input id="bl-num" autoFocus required value={draft.number}
                onChange={(ev) => setDraft({ ...draft, number: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="bl-ref">Vendor reference</FieldLabel>
              <Input id="bl-ref" value={draft.vendor_reference}
                onChange={(ev) => setDraft({ ...draft, vendor_reference: ev.target.value })}
                className="font-mono" placeholder="vendor's own number" />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="bl-vendor">Vendor *</FieldLabel>
            <SearchableSelect
              id="bl-vendor"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v) })}
              placeholder="— select —"
              options={vendors.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="bl-date">Bill date *</FieldLabel>
              <DatePicker id="bl-date" required value={draft.bill_date}
                onChange={(v) => setDraft({ ...draft, bill_date: v })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="bl-due">Due date</FieldLabel>
              <DatePicker id="bl-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="bl-cur">Currency</FieldLabel>
              <Input id="bl-cur" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    number: '',
    vendor_reference: '',
    partner: 0,
    bill_date: localISODate(new Date()),
    due_date: null,
    currency_code: 'EUR',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
